import Box from "@mui/material/Box";
import { FC, ReactNode } from "react";
import { useSidebarStore } from "../../store/sidebar/sidebarStore";
import {
  SIDEBAR_DIMENSIONS,
  ANIMATION_DURATION,
  TRANSITIONS,
  BRAND_COLORS,
} from "./constants";
import { UILogo } from "./UILogo";
import { NavigationItem } from "./NavigationItem";
import { SidebarToggler } from "./SidebarToggler";
import {
  shouldShowDivider,
  shouldShowSectionLabel,
  getSectionStyles,
  flattenNavigation,
  type NavigationConfig,
} from "./sidebarUtils";

interface AppSidebarProps {
  navigationConfig: NavigationConfig;
  logo?: ReactNode;
  logoHref?: string;
  collapseButton?: ReactNode | null;
}

export const AppSidebar: FC<AppSidebarProps> = ({
  navigationConfig,
  logo,
  logoHref,
  collapseButton,
}) => {
  const { status } = useSidebarStore();
  const isExpanded = status === "opened";
  const width = isExpanded
    ? SIDEBAR_DIMENSIONS.MAIN_EXPANDED_PX
    : SIDEBAR_DIMENSIONS.MAIN_COLLAPSED_PX;

  const allItems = flattenNavigation(navigationConfig);

  const logoSection = logo ? (
    <Box
      id="sidebar-logo"
      sx={{
        height: SIDEBAR_DIMENSIONS.LOGO_SECTION_HEIGHT,
        // Border inside the 64px, as the topbar's is, so both rules share a row.
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: `1px solid ${BRAND_COLORS.PRUSSIAN}`,
        flexShrink: 0,
        position: "relative",
        // The divider between the logo and the topbar, inset 12px top and
        // bottom. It stands in for the sidebar's own right border in the band.
        "&::after": {
          content: '""',
          position: "absolute",
          top: "12px",
          bottom: "12px",
          right: 0,
          width: "1px",
          backgroundColor: BRAND_COLORS.PRUSSIAN,
        },
      }}
    >
      <UILogo
        logo={logo}
        href={logoHref}
        isCollapsed={!isExpanded}
        style={{
          flexShrink: 0,
          transition: `opacity ${ANIMATION_DURATION}ms`,
        }}
      />
    </Box>
  ) : null;

  return (
    <Box
      component="aside"
      sx={{
        width: `${width}px`,
        height: "100%",
        backgroundColor: "#ffffff",
        position: "relative",
        // The right border starts below the logo band, where the prussian
        // divider takes over.
        "&::after": {
          content: '""',
          position: "absolute",
          top: SIDEBAR_DIMENSIONS.LOGO_SECTION_HEIGHT,
          bottom: 0,
          right: 0,
          width: "1px",
          backgroundColor: "#e5e7eb",
          pointerEvents: "none",
        },
        display: "flex",
        flexDirection: "column",
        transition: `width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        flexShrink: 0,
        overflow: "hidden",
        fontFamily:
          "Avenir, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <Box
        sx={{
          height: "100%",
          overflow: "hidden",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          width: `${width}px`,
          transition: `width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        }}
      >
        {logoSection}

        <Box
          component="nav"
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: isExpanded ? "16px 12px" : "8px 12px",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            transition: TRANSITIONS.SIDEBAR,
          }}
        >
          {allItems.map((item, index) => {
            const isFirstBottomLink =
              item.isBottom && (index === 0 || !allItems[index - 1].isBottom);
            const showDivider = shouldShowDivider(
              item,
              index,
              allItems,
              isExpanded
            );
            const showSectionLabel = shouldShowSectionLabel(
              item,
              index,
              allItems,
              isExpanded
            );

            // Group bottom links in a single container
            if (isFirstBottomLink) {
              const bottomLinks = allItems.filter((i) => i.isBottom);
              return (
                <Box
                  key="bottom-links-group"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    borderTop: "1px solid #e5e7eb",
                    marginTop: "auto",
                    paddingTop: isExpanded ? "24px" : "8px",
                    transition: TRANSITIONS.PADDING,
                  }}
                >
                  {bottomLinks.map((bottomItem) => (
                    <NavigationItem
                      key={bottomItem.href}
                      item={bottomItem}
                      isExpanded={isExpanded}
                      showTooltip={bottomItem.label !== "Back Home"}
                    />
                  ))}
                </Box>
              );
            }

            // Skip rendering if this is a bottom link (already rendered in group above)
            if (item.isBottom) {
              return null;
            }

            return (
              <Box
                key={`${item.href}-${index}`}
                sx={getSectionStyles(
                  item,
                  isExpanded,
                  navigationConfig,
                  showSectionLabel
                )}
              >
                {showDivider && (
                  <Box
                    sx={{
                      height: "1px",
                      backgroundColor: "#e5e7eb",
                      margin: "8px 0",
                    }}
                  />
                )}
                {showSectionLabel && item.section && (
                  <Box
                    sx={{
                      padding: "8px 12px",
                      fontSize: "14px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#1f1f1f",
                      lineHeight: 1,
                    }}
                  >
                    {item.section}
                  </Box>
                )}
                <NavigationItem
                  item={item}
                  isExpanded={isExpanded}
                  showTooltip={item.label !== "Back Home"}
                />
              </Box>
            );
          })}
        </Box>

        {collapseButton !== null && (
          <Box
            sx={{
              borderTop: "1px solid #e5e7eb",
              padding: isExpanded ? "12px" : "8px 12px",
              flexShrink: 0,
              transition: TRANSITIONS.SIDEBAR,
            }}
          >
            {collapseButton || <SidebarToggler />}
          </Box>
        )}
      </Box>
    </Box>
  );
};
